import type { OptionListDto } from '@mimir/common/dto/optionListDto';

/**
 * Fetch the option lists referenced by `ids` that are not already present,
 * deduplicating against both already-loaded lists and fetches currently in
 * flight from concurrent callers.
 *
 * The crucial behaviour is that an id already being fetched by another caller
 * is awaited, not skipped. A previous version skipped in-flight ids without
 * awaiting them, which let a caller (e.g. the metadata editor) proceed and
 * render fields before their option lists were committed to the store. On a
 * hard page refresh the boot-time fetch of the default schema's option lists
 * races the item editor's fetch, so the editor saw missing option lists and
 * flagged every option-list-driven field as "invalid value found".
 *
 * `ongoing` is mutated in place: an entry is added while a fetch is pending and
 * cleared once it settles. The returned array contains every option list that
 * resolved successfully (including those awaited from concurrent fetches);
 * fetches that reject are omitted.
 */
export async function fetchMissingOptionLists(
  existing: OptionListDto[],
  ongoing: Record<string, Promise<OptionListDto> | undefined>,
  ids: string[],
  fetchOptionList: (id: string) => Promise<OptionListDto>
): Promise<OptionListDto[]> {
  const existingIds = new Set(existing.map(ol => ol.id));
  const uniqueIds = [...new Set(ids)];
  const promises: Promise<OptionListDto>[] = [];
  for (const id of uniqueIds) {
    if (existingIds.has(id)) continue;
    const inFlight = ongoing[id];
    if (inFlight) {
      promises.push(inFlight);
      continue;
    }
    const fetch = fetchOptionList(id).finally(() => {
      ongoing[id] = undefined;
    });
    ongoing[id] = fetch;
    promises.push(fetch);
  }

  const settled = await Promise.allSettled(promises);
  const fetched: OptionListDto[] = [];
  for (const result of settled) {
    if (result.status === 'fulfilled') {
      fetched.push(result.value);
    }
  }
  return fetched;
}
